import { type MavCmd, type MissionItemInt } from "../gen/schema";
import { calculateBestGlideSpeed } from "./calculations";
import { range } from "./common";
import { baseDistance, turnDistance } from "./configuration";
import {
  adjustedDistance,
  climb,
  groundBearing,
  groundDistance,
  move,
  normalizeAngle,
  setAltitude,
  toRadians,
} from "./math";
import type { Drop, Position } from "./model";
import { positionEmpty } from "./position";

export const missionPaths = (items: MissionItemInt[]) => {
  const groups: Position[][] = [];
  let current: Position[] = [];

  items.forEach((item, i) => {
    if (i === 0 || item.command === "DO_LAND_START") return;

    const position = itemPosition(item);
    if (position) current.push(position);

    const terminal = item.command === "NAV_LAND";

    if (terminal && current.length > 0) {
      groups.push(current);
      current = [];
    }
  });

  if (current.length > 0) groups.push(current);

  return groups;
};

export const itemPosition = (item: MissionItemInt) => {
  const lat = item.x / 1e7;
  const lon = item.y / 1e7;
  const altitude = item.z;

  if (lat === 0 && lon === 0) return undefined;

  return [lon, lat, altitude] satisfies Position | undefined;
};

const missionItem = (
  [lon, lat, altitude]: Position,
  command: MavCmd = "NAV_WAYPOINT",
) =>
  ({
    targetSystem: 0,
    targetComponent: 0,
    seq: 0,
    frame: "GLOBAL_INT",
    command,
    current: 0,
    autocontinue: 0,
    param1: 0,
    param2: 0,
    param3: 0,
    param4: 0,
    x: lat * 1e7,
    y: lon * 1e7,
    z: altitude,
    missionType: "MISSION",
  }) satisfies MissionItemInt;

export const dropMission = (drop: Drop) => {
  const {
    start,
    end,
    dropDirection,
    windSpeed,
    windDirection,
    bend1,
    bend2,
    dropAltitude,
    glideSlope,
    bidirectional,
  } = drop;
  if (positionEmpty(start) || positionEmpty(end) || positionEmpty(drop.drop))
    return [];
  const [lon = 0, lat = 0] = drop.drop;

  const runwayLength = groundDistance(start, end);
  const [, , startAltitude] = start;
  const [, , endAltitude] = end;
  const runwayCenter = setAltitude(
    move(start, 0.5 * runwayLength, groundBearing(start, end)),
    0.5 * (startAltitude + endAltitude),
  );

  const position: Position = [lon, lat, dropAltitude];

  const stepRatio = 0.5;
  const flip =
    Math.sin(groundBearing(start, position) - groundBearing(start, end)) < 0;

  const home = missionItem(end);

  const speed = calculateBestGlideSpeed(drop);

  const createDirection = ({ primary }: { primary: boolean }) => {
    const land = primary ? end : start;
    const [, , targetAltitude] = runwayCenter;

    const landBearing = primary
      ? groundBearing(end, start)
      : groundBearing(start, end);

    const bearing = landBearing + (primary ? bend1 : bend2);

    const turn = move(land, 0.5 * runwayLength + turnDistance, landBearing);

    const calculateApproachDistance = () => {
      let result = 1000;
      const targetDistance = 1.3 * glideSlope * (dropAltitude - targetAltitude);
      for (let i = 0; i < 10; i++) {
        const location = move(turn, result, bearing);
        const distance =
          adjustedDistance(location, turn, speed, windSpeed, windDirection) +
          adjustedDistance(
            turn,
            runwayCenter,
            speed,
            windSpeed,
            windDirection,
          ) +
          adjustedDistance(position, location, speed, windSpeed, windDirection);
        result = result * (targetDistance / distance);
      }

      return Math.max(result, 3 * runwayLength);
    };

    const approachDistance = calculateApproachDistance();

    const step = stepRatio * runwayLength;
    const alternates = Math.min(70, Math.floor(approachDistance / step));

    return [
      missionItem(position, "DO_LAND_START"),
      ...range(0, alternates + 1).flatMap(i => {
        const final = move(turn, approachDistance - step * i, bearing);
        const base = move(
          final,
          baseDistance,
          bearing + ((flip !== primary ? -1 : 1) * Math.PI) / 2,
        );
        const path = flightPath(
          [base, final, turn, land],
          drop.drop,
          toRadians(dropDirection),
        );
        return [
          missionItem([0, 0, 0], "JUMP_TAG"),
          ...path.map((_, i) =>
            missionItem(_, i === path.length - 1 ? "NAV_LAND" : undefined),
          ),
        ];
      }),
    ];
  };

  const flightPath = (
    positions: Position[],
    last: Position,
    lastBearing: number,
  ) => {
    const results: Position[] = [];
    let altitude = 0;

    for (let i = 0; i < positions.length; i++) {
      const position = positions[i] ?? [0, 0, 0];
      const bearing = groundBearing(last, position);
      const turnLoss =
        180 * Math.abs(normalizeAngle(bearing - lastBearing) / Math.PI);
      altitude -= turnLoss;
      if (i > 0) results.push(setAltitude(last, altitude));

      const distance = adjustedDistance(
        last,
        position,
        speed,
        windSpeed,
        windDirection,
      );
      if (distance <= 0) return [];
      const glideLoss = distance / glideSlope;
      altitude -= glideLoss;
      if (i > 0) results.push(setAltitude(position, altitude));

      last = position;
      lastBearing = bearing;
    }
    const [[, , targetAltitude = 0] = []] = positions.slice(-1);
    return results.map(_ => climb(_, targetAltitude - altitude));
  };

  return [
    home,
    ...createDirection({ primary: true }),
    ...(bidirectional ? createDirection({ primary: false }) : []),
    ...(!bidirectional ? [missionItem(start, "NAV_LAND")] : []),
  ];
};
